import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApi } from '../../hooks';
import { api } from '../../services/api';
import KPIWidget from '../common/KPIWidget';
import TrainingGuide from '../common/TrainingGuide';
import attendanceSteps from '../../data/training/attendance';
import { BRAND } from '../../branding';
import TableMobile, { TableMobileColumn, StatusTone } from '../responsive-fixes/TableMobile';
import { useResponsiveLayout } from '../responsive-fixes/useResponsiveLayout';

interface AttendanceLog {
  id: string;
  employee_name: string;
  shift_type: string;
  scheduled_time: string;
  actual_time: string;
  minutes_offset: number;
  within_grace: boolean;
  tactical_delay: boolean;
  created_at: string;
}

interface Employee {
  id: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
}

interface EmpKPI {
  name: string;
  role: string;
  total_logs: number;
  on_time: number;
  delayed: number;
  kpi: number;
}

interface PingStatus {
  name: string;
  status: 'green' | 'yellow' | 'red';
  last_ping: string | null;
  minutes_ago: number | null;
}

const PING_DOT_COLORS: Record<string, string> = {
  green:  '#22c55e',
  yellow: '#f59e0b',
  red:    '#ef4444',
};

const PING_DOT_LABELS: Record<string, string> = {
  green:  'Online (< 15 min)',
  yellow: 'Idle (15–60 min)',
  red:    'Offline (> 60 min)',
};

const SHIFT_LABELS: Record<string, string> = {
  morning_in: 'Morning In',
  morning_out: 'Morning Out',
  evening_in: 'Evening In',
  evening_out: 'Evening Out',
};

const AttendanceDashboard: React.FC = () => {
  const responsive = useResponsiveLayout();
  const useMobileTable = responsive.isPhone || responsive.isPhablet;
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [pingMap, setPingMap] = useState<Record<string, PingStatus>>({});
  const pingIntervalRef = useRef<number | null>(null);
  const [filterName, setFilterName] = useState('');
  const [addName, setAddName] = useState('');
  const [addRole, setAddRole] = useState('employee');
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [userRole, setUserRole] = useState<string>('');

  // Get user role from auth context
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const res = await api.auth.me();
        if (res.role) {
          setUserRole(res.role);
        }
      } catch (error) {
        console.error('Failed to fetch user role:', error);
      }
    };
    fetchUserRole();
  }, []);

  const { data: kpiData,   loading: kpiLoading,   refetch: refetchKpi }    = useApi(() => api.employee.kpi(days));
  const { data: perEmpData, loading: perLoading,  refetch: refetchPer }    = useApi(() => api.employee.kpiPerEmployee(days));
  const { data: logsData,  loading: logsLoading,  refetch: refetchLogs }   = useApi(() => api.employee.logs(7, filterName || undefined));
  const { data: rosterData, loading: rosterLoading, refetch: refetchRoster } = useApi(() => api.employee.roster());

  const refreshAll = useCallback(() => {
    refetchKpi();
    refetchPer();
    refetchLogs();
    refetchRoster();
    setLastRefresh(new Date());
  }, [refetchKpi, refetchPer, refetchLogs, refetchRoster]);

  // Refetch when days change
  useEffect(() => {
    refreshAll();
  }, [days, refreshAll]);

  // Refetch logs when filter changes
  useEffect(() => {
    refetchLogs();
  }, [filterName, refetchLogs]);

  // ── Ping status polling (every 60s) ─────────────────────────────────────────
  useEffect(() => {
    const fetchPingStatus = async () => {
      try {
        const res = await fetch('/api/employee/ping-status', { 
          credentials: 'include',
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        if (!res.ok) {
          console.warn(`Ping status fetch failed: ${res.status}`);
          return;
        }
        const data = await res.json();
        const map: Record<string, PingStatus> = {};
        for (const entry of data.employees || []) {
          map[entry.name] = entry;
        }
        setPingMap(map);
      } catch (error) {
        console.warn('Ping status fetch error:', error);
        // Silent fail for non-critical feature, but log to console
      }
    };
    
    fetchPingStatus(); // Initial fetch
    pingIntervalRef.current = window.setInterval(fetchPingStatus, 60_000);
    
    return () => { 
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, []);

  const formatDateTime = useMemo(() => {
    return (iso: string) =>
      new Date(iso).toLocaleString('en-US', {
        timeZone: 'Asia/Dubai', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
  }, []);

  const kpiColor = useMemo(() => {
    return (k: number) => k >= 90 ? '#22c55e' : k >= 70 ? '#f59e0b' : '#ef4444';
  }, []);

  const handleAddEmployee = async () => {
    if (!addName.trim()) return;
    
    // Basic email validation
    if (addEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addEmail)) {
      setAddError('Please enter a valid email address');
      return;
    }

    setAdding(true); 
    setAddError(''); 
    setAddSuccess('');
    try {
      await api.employee.addEmployee(addName.trim(), addRole, addEmail || undefined);
      setAddSuccess(`${addName} added successfully`);
      setAddName(''); 
      setAddEmail(''); 
      setAddRole('employee');
      refreshAll();
    } catch (e: any) {
      setAddError(e.message || 'Failed to add employee');
      console.error('Add employee error:', e);
    } finally {
      setAdding(false);
    }
  };

  const handleDeactivate = async (emp: Employee) => {
    if (!window.confirm(`Deactivate ${emp.name}?`)) return;
    
    setDeactivatingId(emp.id);
    try {
      await api.employee.updateEmployee(emp.id, { is_active: false });
      refreshAll();
    } catch (error: any) {
      console.error('Deactivate error:', error);
      alert(`Failed to deactivate ${emp.name}: ${error.message || 'Unknown error'}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleReactivate = async (emp: Employee) => {
    if (!window.confirm(`Reactivate ${emp.name}?`)) return;
    
    setReactivatingId(emp.id);
    try {
      await api.employee.updateEmployee(emp.id, { is_active: true });
      refreshAll();
    } catch (error: any) {
      console.error('Reactivate error:', error);
      alert(`Failed to reactivate ${emp.name}: ${error.message || 'Unknown error'}`);
    } finally {
      setReactivatingId(null);
    }
  };

  const isAdmin = userRole === 'super_admin' || userRole === 'owner' || userRole === 'manager';

  return (
    <div className="attendance-dashboard">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Attendance &amp; Employee Management</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: BRAND.colors.textMuted }}>
            Last refreshed: {lastRefresh.toLocaleTimeString('en-US', { timeZone: 'Asia/Dubai' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            onClick={refreshAll}
            className="btn btn-secondary"
            style={{ fontSize: '0.85rem' }}
          >
            Refresh
          </button>
          <button 
            onClick={() => setTrainingOpen(true)} 
            title="Open training guide" 
            style={{ 
              background: 'rgba(255,214,165,0.1)', 
              border: '1px solid rgba(255,214,165,0.2)', 
              borderRadius: 8, 
              color: '#ffd6a5', 
              cursor: 'pointer', 
              fontSize: 14, 
              fontWeight: 700, 
              padding: '3px 10px' 
            }}
          >
            ?
          </button>
        </div>
      </div>
      <TrainingGuide moduleKey="attendance" steps={attendanceSteps} isOpen={trainingOpen} onClose={() => setTrainingOpen(false)} />

      {/* ── Period selector ── */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>Period:</span>
        {[7, 14, 30, 90].map(d => (
          <button
            key={d}
            className={`btn ${days === d ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setDays(d)}
            style={{ padding: '4px 14px', fontSize: '0.85rem' }}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* ── Aggregate KPIs ── */}
      <div className="kpi-row">
        <KPIWidget
          title="Attendance KPI"
          value={kpiData ? `${kpiData.kpi_attendance}%` : '—'}
          subtitle={`Last ${days} days`}
          accent={!!(kpiData && kpiData.kpi_attendance >= 90)}
          loading={kpiLoading}
        />
        <KPIWidget
          title="Total Check-ins"
          value={kpiData?.total_logs ?? '—'}
          subtitle="Logged entries"
          loading={kpiLoading}
        />
        <KPIWidget
          title="On Time"
          value={kpiData?.on_time_logs ?? '—'}
          subtitle="Within grace window"
          accent={true}
          loading={kpiLoading}
        />
        <KPIWidget
          title="Tactical Delays"
          value={kpiData?.delayed_logs ?? '—'}
          subtitle="Late check-ins"
          accent={false}
          loading={kpiLoading}
        />
      </div>

      {/* ── Per-employee KPI ── */}
      <div className="card">
        <h2>👥 Per-Employee KPI</h2>
        {perLoading ? (
          <p className="text-muted">Loading…</p>
        ) : (perEmpData?.employees?.length ?? 0) > 0 ? (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Check-ins</th>
                  <th>On Time</th>
                  <th>Delayed</th>
                  <th>KPI</th>
                </tr>
              </thead>
              <tbody>
                {perEmpData.employees.map((e: EmpKPI) => (
                  <tr key={e.name}>
                    <td style={{ fontWeight: 600 }}>{e.name}</td>
                    <td><span className="badge badge--info">{e.role}</span></td>
                    <td>{e.total_logs}</td>
                    <td style={{ color: '#22c55e' }}>{e.on_time}</td>
                    <td style={{ color: e.delayed > 0 ? '#ef4444' : 'inherit' }}>{e.delayed}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: kpiColor(e.kpi) }}>
                        {e.kpi}%
                      </span>
                      <div style={{ height: 4, background: '#1e2a45', borderRadius: 2, marginTop: 3, width: 80 }}>
                        <div style={{ height: '100%', width: `${e.kpi}%`, background: kpiColor(e.kpi), borderRadius: 2 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">No employees registered yet. Add employees below.</p>
        )}
      </div>

      {/* ── Employee roster ── */}
      <div className="card">
        <h2>🗂️ Employee Roster</h2>
        {rosterLoading ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Online</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {(rosterData?.employees || []).map((emp: Employee) => {
                  const ping = pingMap[emp.name];
                  const dotColor = ping ? PING_DOT_COLORS[ping.status] : '#334155';
                  const dotTitle = ping
                    ? `${PING_DOT_LABELS[ping.status]}${ping.minutes_ago != null ? ` — ${ping.minutes_ago}min ago` : ''}`
                    : 'No ping recorded';
                  return (
                  <tr key={emp.id}>
                    <td style={{ fontWeight: 600 }}>{emp.name}</td>
                    <td><span className="badge badge--info">{emp.role}</span></td>
                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>{emp.email}</td>
                    <td>
                      {emp.is_active
                        ? <span className="badge badge--success">Active</span>
                        : <span className="badge badge--error">Inactive</span>}
                    </td>
                    <td title={dotTitle} style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        width: 10, height: 10,
                        borderRadius: '50%',
                        background: dotColor,
                        boxShadow: ping?.status === 'green' ? `0 0 6px ${dotColor}` : 'none',
                      }} />
                    </td>
                    {isAdmin && (
                      <td>
                        {emp.is_active ? (
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '2px 10px', fontSize: '0.78rem' }}
                            onClick={() => handleDeactivate(emp)}
                            disabled={deactivatingId === emp.id}
                          >
                            {deactivatingId === emp.id ? 'Deactivating...' : 'Deactivate'}
                          </button>
                        ) : (
                          <button
                            className="btn btn-primary"
                            style={{ padding: '2px 10px', fontSize: '0.78rem' }}
                            onClick={() => handleReactivate(emp)}
                            disabled={reactivatingId === emp.id}
                          >
                            {reactivatingId === emp.id ? 'Reactivating...' : 'Reactivate'}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
                {(!rosterData?.employees || rosterData.employees.length === 0) && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="text-muted">No employees yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Add employee form - only for admins */}
        {isAdmin && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <h3 style={{ marginBottom: 12 }}>➕ Add Employee</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
                <label>Name *</label>
                <input
                  className="form-control"
                  placeholder="Full name"
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddEmployee()}
                />
              </div>
              <div className="form-group" style={{ margin: 0, flex: '1 1 120px' }}>
                <label>Role</label>
                <select className="form-control" value={addRole} onChange={e => setAddRole(e.target.value)}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <div className="form-group" style={{ margin: 0, flex: '2 1 200px' }}>
                <label>Email (optional)</label>
                <input
                  className="form-control"
                  placeholder="email@afaq.com"
                  value={addEmail}
                  onChange={e => setAddEmail(e.target.value)}
                  type="email"
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleAddEmployee}
                disabled={adding || !addName.trim()}
                style={{ alignSelf: 'flex-end' }}
              >
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
            {addError   && <p style={{ color: '#ef4444', marginTop: 8, fontSize: '0.85rem' }}>{addError}</p>}
            {addSuccess && <p style={{ color: '#22c55e', marginTop: 8, fontSize: '0.85rem' }}>{addSuccess}</p>}
          </div>
        )}
      </div>

      {/* ── KPI formula ── */}
      <div className="card">
        <h2>📊 KPI Formula</h2>
        <p className="text-muted">
          KPI_attendance = (On-Time Check-ins ÷ Total Check-ins) × 100
        </p>
        {kpiData && (
          <p className="text-muted">
            = ({kpiData.on_time_logs} ÷ {kpiData.total_logs}) × 100 = {kpiData.kpi_attendance}%
          </p>
        )}
        <p className="text-muted" style={{ marginTop: 8 }}>
          Grace window: 15 min · Shifts: 09:00 / 14:00 / 16:30 / 21:30 · Friday: morning out 12:00
        </p>
      </div>

      {/* ── Logs ── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>📋 Recent Logs (7 days)</h2>
          <select
            className="form-control"
            style={{ width: 180 }}
            value={filterName}
            onChange={e => setFilterName(e.target.value)}
            disabled={!rosterData?.employees?.length}
          >
            <option value="">All employees</option>
            {rosterData?.employees?.map((e: Employee) => (
              <option key={e.id} value={e.name}>{e.name}</option>
            ))}
          </select>
        </div>

        {logsLoading ? (
          <p className="text-muted">Loading…</p>
        ) : logsData?.logs?.length > 0 ? (
          useMobileTable ? (
            // M3 fix (audit): attendance log table is unreadable below md.
            <TableMobile<any>
              columns={[
                { key: 'employee_name', label: 'Employee', primary: true },
                { key: 'tactical_delay', label: 'Status', status: true,
                  format: (_v: unknown, row: AttendanceLog) =>
                    row.tactical_delay ? '🚨 Late' : row.within_grace ? '✓ On Time' : '⚠ Borderline',
                },
                { key: 'shift_type', label: 'Shift',
                  format: (v: unknown) => SHIFT_LABELS[v as string] || (v as string),
                },
                { key: 'scheduled_time', label: 'Scheduled',
                  format: (v: unknown) => formatDateTime(v as string),
                },
                { key: 'actual_time', label: 'Actual',
                  format: (v: unknown) => formatDateTime(v as string),
                },
                { key: 'minutes_offset', label: 'Offset',
                  format: (v: unknown) => {
                    const n = v as number;
                    return `${n > 0 ? '+' : ''}${n.toFixed(1)}m`;
                  },
                },
              ] as TableMobileColumn<any>[]}
              rows={logsData.logs}
              statusTone={(row: AttendanceLog): StatusTone =>
                row.tactical_delay ? 'danger' : row.within_grace ? 'success' : 'warning'
              }
            />
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Shift</th>
                    <th>Scheduled</th>
                    <th>Actual</th>
                    <th>Offset</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logsData.logs.map((log: AttendanceLog) => (
                    <tr
                      key={log.id}
                      className={log.tactical_delay ? 'row-error' : log.within_grace ? 'row-success' : ''}
                    >
                      <td>{log.employee_name}</td>
                      <td>{SHIFT_LABELS[log.shift_type] || log.shift_type}</td>
                      <td>{formatDateTime(log.scheduled_time)}</td>
                      <td>{formatDateTime(log.actual_time)}</td>
                      <td style={{ color: log.minutes_offset > 15 ? '#ef4444' : log.minutes_offset < -5 ? '#22c55e' : 'inherit' }}>
                        {log.minutes_offset > 0 ? '+' : ''}{log.minutes_offset.toFixed(1)}m
                      </td>
                      <td>
                        {log.tactical_delay ? (
                          <span className="badge badge--error">🚨 Late</span>
                        ) : log.within_grace ? (
                          <span className="badge badge--success">✓ On Time</span>
                        ) : (
                          <span className="badge badge--warning">⚠ Borderline</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <p className="text-muted">No logs found</p>
        )}
      </div>
    </div>
  );
};

export default AttendanceDashboard;